import { useEffect, useRef, useState } from "react";
import { ChevronRight, Folder, FolderLock, Image, Loader2 } from "lucide-react";
import {
  fetchCoffreFolders,
  fetchCoffreUnlocked,
  fetchPhotosFolders,
} from "@/lib/suite/saveChatAttachment";

type Kind = "coffre" | "photos";

type Props = {
  open: boolean;
  kind: Kind;
  filename?: string;
  onClose: () => void;
  onConfirm: (folderPath: string) => void | Promise<void>;
};

export function ChatDestFolderPicker({ open, kind, filename, onClose, onConfirm }: Props) {
  const [path, setPath] = useState("");
  const [folders, setFolders] = useState<Array<{ name: string; path: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const openedAt = useRef(0);

  useEffect(() => {
    if (!open) return;
    openedAt.current = Date.now();
    setPath("");
    setFolders([]);
    setError(null);
    setLocked(false);
    void reload("");
  }, [open, kind]);

  async function reload(nextPath: string) {
    setLoading(true);
    setError(null);
    try {
      if (kind === "coffre") {
        const st = await fetchCoffreUnlocked();
        if (!st.ok || !st.unlocked) {
          setLocked(true);
          setFolders([]);
          setError(st.message ?? "Déverrouillez le Coffre avec votre code d’accès.");
          return;
        }
        setLocked(false);
        const result = await fetchCoffreFolders(nextPath);
        if (result.error) {
          setError(result.error);
          setFolders([]);
          return;
        }
        setPath(result.path);
        setFolders(result.folders);
        return;
      }
      const result = await fetchPhotosFolders(nextPath);
      if (result.error) {
        setError(result.error);
        setFolders([]);
        return;
      }
      setPath(result.path);
      setFolders(result.folders);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lecture impossible.");
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  const title = kind === "coffre" ? "Enregistrer dans le Coffre" : "Enregistrer dans Photos";
  const crumbs = path ? path.split("/").filter(Boolean) : [];
  const Icon = kind === "coffre" ? FolderLock : Image;

  return (
    <div
      className="chat-confirm-overlay fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => {
        // iOS : le click fantôme du tiroir atterrit ici et fermait le picker tout de suite.
        if (Date.now() - openedAt.current < 480) return;
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      onPointerDown={(e) => {
        if (Date.now() - openedAt.current < 480) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div
        className="chat-confirm-dialog flex max-h-[min(78vh,32rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-[var(--chat-border)] bg-[var(--chat-surface)] shadow-[var(--chat-shadow)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-dest-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-[var(--chat-border)] px-4 py-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]">
            <Icon className="size-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="chat-dest-title" className="truncate text-sm font-semibold text-[var(--chat-text)]">
              {title}
            </h2>
            <p className="mt-0.5 truncate text-[11px] text-[var(--chat-muted)]">
              {filename || "Choisissez le dossier de destination"}
            </p>
          </div>
          {loading ? <Loader2 className="size-4 shrink-0 animate-spin text-[var(--chat-muted)]" /> : null}
        </div>

        <div className="flex flex-wrap items-center gap-1 border-b border-[var(--chat-border)] px-4 py-2 text-xs text-[var(--chat-muted)]">
          <button type="button" className="rounded px-1 py-0.5 hover:text-[var(--chat-accent-strong)]" onClick={() => void reload("")}>
            {kind === "coffre" ? "Coffre" : "Photos"}
          </button>
          {crumbs.map((part, index) => {
            const crumbPath = crumbs.slice(0, index + 1).join("/");
            return (
              <span key={crumbPath} className="inline-flex items-center gap-1">
                <ChevronRight className="size-3 opacity-60" aria-hidden />
                <button
                  type="button"
                  className="rounded px-1 py-0.5 hover:text-[var(--chat-accent-strong)]"
                  onClick={() => void reload(crumbPath)}
                >
                  {part}
                </button>
              </span>
            );
          })}
        </div>

        {error ? (
          <p className="px-4 pt-2 text-xs text-[var(--chat-danger)]" role="alert">
            {error}
            {locked ? " Ouvrez le Coffre pour le déverrouiller, puis réessayez." : null}
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {loading && folders.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--chat-muted)]">
              <Loader2 className="size-4 animate-spin" />
              Chargement…
            </div>
          ) : folders.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-[var(--chat-muted)]">
              {locked ? "Coffre verrouillé." : "Aucun sous-dossier — vous pouvez enregistrer ici."}
            </p>
          ) : (
            <ul className="space-y-0.5">
              {folders.map((folder) => (
                <li key={folder.path}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[var(--chat-text)] hover:bg-[var(--chat-surface-elevated)]"
                    onClick={() => void reload(folder.path)}
                  >
                    <Folder className="size-4 shrink-0 text-[var(--chat-muted)]" />
                    <span className="truncate">{folder.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--chat-border)] px-4 py-3">
          <button
            type="button"
            disabled={busy}
            className="rounded-xl px-4 py-2 text-sm font-medium text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)] disabled:opacity-50"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={busy || locked}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--chat-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={() => {
              void (async () => {
                setBusy(true);
                setError(null);
                try {
                  await onConfirm(path);
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Enregistrement impossible.");
                  setBusy(false);
                  return;
                }
                setBusy(false);
              })();
            }}
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            Enregistrer ici
          </button>
        </div>
      </div>
    </div>
  );
}
